import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, MailCheck, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyEmailPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (user?.email_verified) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-email?code=${code}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email verified successfully!');
      // Refresh user data by reloading
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification email resent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend');
    }
    setResending(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-gradient-to-br from-pink-50/80 to-white">
      <Card className="w-full max-w-md border-pink-100/60 rounded-2xl shadow-soft" data-testid="verify-email-card">
        <CardHeader className="text-center">
          <div className="mx-auto p-4 bg-primary/8 rounded-full w-fit mb-4">
            <MailCheck className="h-10 w-10 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="font-heading text-3xl">Verify Your Email</CardTitle>
          <CardDescription>
            We sent a 6-digit code to <strong>{user?.email}</strong>. Enter it below to verify your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="space-y-2">
              <Label>Verification Code</Label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                data-testid="verify-code-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full btn-lift"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-btn"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verify Email
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground mb-2">Didn't receive the code?</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending}
              className="rounded-full"
              data-testid="resend-code-btn"
            >
              {resending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Resend Code
            </Button>
          </div>
          <div className="mt-4 text-center">
            <Button variant="link" size="sm" onClick={() => navigate('/dashboard')} className="text-muted-foreground">
              Skip for now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
